import type { VehicleBlueprint } from "../types/VehicleBlueprint.js";
import { blueprintToBaseWeaponStats } from "./defaultWeaponStats.js";
import { DEFAULT_WEAPON_UPGRADES, type WeaponStats, type WeaponUpgrade } from "./WeaponStats.js";
import { WEAPON_UPGRADE_MODIFIERS } from "./weaponUpgradeTable.js";

/**
 * Compute final runtime WeaponStats for a vehicle.
 *
 * Upgrade multipliers are applied to damage, reloadMs, projectileSpeed, and range.
 * projectileLifetimeMs is always re-derived last as (range / projectileSpeed) × 1000,
 * so it correctly reflects both range and velocity upgrades.
 *
 * With DEFAULT_WEAPON_UPGRADES the result is baseline-equivalent to the blueprint's
 * raw weapon fields and the global PROJECTILE_LIFETIME_MS constant.
 */
export function computeWeaponStats(
  blueprint: VehicleBlueprint,
  upgrades: WeaponUpgrade = DEFAULT_WEAPON_UPGRADES,
): WeaponStats {
  const base = blueprintToBaseWeaponStats(blueprint);
  const result = { ...base } as Record<keyof WeaponStats, number>;

  for (const slot of Object.keys(upgrades) as (keyof WeaponUpgrade)[]) {
    const level = upgrades[slot];
    if (!level) continue;
    const slotMods = WEAPON_UPGRADE_MODIFIERS[slot];
    for (const statKey of Object.keys(slotMods) as (keyof WeaponStats)[]) {
      const levelMods = slotMods[statKey];
      if (levelMods === undefined) continue;
      result[statKey] *= levelMods[level - 1];
    }
  }

  // Always re-derive so it reflects the combined effect of range and velocity upgrades.
  result.projectileLifetimeMs = (result.range / result.projectileSpeed) * 1000;

  // Streams counts: no upgrade=1, L1=2, L2+=5
  const STREAM_COUNTS = [1, 2, 5] as const;
  const streams = STREAM_COUNTS[Math.min(2, upgrades?.streams ?? 0)];

  return { ...result, streams } as WeaponStats;
}
